/** What this device remembers about a preview page, for the next visit.
 *
 *  Buying happens on a later visit: the people who order open their page a
 *  median of six times over a day or two, and the ones who open it once
 *  almost never do. So the page keeps, on the device only, the size they last
 *  picked and how many times it has been opened here, and the order button
 *  comes back saying "Order · 4 cm · €29.90" rather than "from €24.90".
 *
 *  Nothing here is sent anywhere: it is read by the page that wrote it and
 *  never by the tracking beacon (track.ts says the same of "minime.previews").
 *  No identity, no email, nothing device-shaped beyond the page's own token,
 *  which is already in the address. Private browsing, or a browser that
 *  refuses storage, simply forgets, and the page is the first-visit page. */

const KEY = "minime.page";

/** How many pages one device keeps. The oldest are dropped past it. */
const KEEP = 12;

export interface PageMemory {
  /** The size last picked in the order sheet, or on a "By size" pill. */
  size?: string;
  /** How far they got in the order sheet: "pay" once they asked for the
   *  payment form, so the next visit's button offers to continue there. */
  step?: "pick" | "pay";
  /** How many times this device has opened the page, this one included. */
  visits: number;
  /** When it was last opened, in milliseconds since the epoch. */
  at: number;
}

type Store = Record<string, PageMemory>;

function stored(): Store {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const out: Store = {};
    for (const [token, entry] of Object.entries(value as Record<string, unknown>)) {
      if (!entry || typeof entry !== "object") continue;
      const e = entry as Partial<PageMemory>;
      out[token] = {
        ...(typeof e.size === "string" && e.size ? { size: e.size } : {}),
        ...(e.step === "pay" || e.step === "pick" ? { step: e.step } : {}),
        visits: Number.isFinite(e.visits) ? Math.max(0, Math.floor(e.visits as number)) : 0,
        at: Number.isFinite(e.at) ? (e.at as number) : 0,
      };
    }
    return out;
  } catch {
    return {}; // private browsing, or something else wrote the key
  }
}

/** Whether it was kept: false where storage is refused, which the visit
 *  count reports as 0 rather than as a first visit. */
function store(all: Store): boolean {
  try {
    const kept = Object.entries(all)
      .sort((a, b) => b[1].at - a[1].at)
      .slice(0, KEEP);
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(kept)));
    return true;
  } catch {
    return false; // storage refused: this visit simply has nothing to remember
  }
}

/** What this device knows of the page, or null for a page never opened here. */
export function remembered(token: string): PageMemory | null {
  if (!token) return null;
  return stored()[token] ?? null;
}

/** This open of the page, counted. Returns the memory as it now is, so the
 *  page can tell a first visit (visits 1) from a return (2 and up). Counted
 *  once per page load: the caller keeps it to a single call. */
export function noteVisit(token: string): PageMemory {
  const all = stored();
  const before = all[token];
  const next: PageMemory = {
    ...(before?.size ? { size: before.size } : {}),
    ...(before?.step ? { step: before.step } : {}),
    visits: (before?.visits ?? 0) + 1,
    at: Date.now(),
  };
  all[token] = next;
  // A device that will not keep it cannot count, and 1 would read as a
  // first visit: 0 says it does not know.
  return store(all) ? next : { ...next, visits: 0 };
}

/** The size they picked, kept for next time. */
export function rememberSize(token: string, size: string): void {
  if (!token || !size) return;
  const all = stored();
  const before = all[token];
  all[token] = { ...before, visits: before?.visits ?? 1, at: before?.at ?? Date.now(), size };
  store(all);
}

/** How far they got, kept for next time: "pay" is never forgotten for
 *  "pick", since having asked for the form once is the fact that matters. */
export function rememberStep(token: string, step: "pick" | "pay"): void {
  if (!token) return;
  const all = stored();
  const before = all[token];
  if (before?.step === "pay" && step === "pick") return;
  all[token] = { ...before, visits: before?.visits ?? 1, at: before?.at ?? Date.now(), step };
  store(all);
}

/** A page that is gone (purged, or a link that opens nothing), forgotten. */
export function forgetPage(token: string): void {
  const all = stored();
  if (!(token in all)) return;
  delete all[token];
  store(all);
}
